import { notifyError } from "@/lib/notify-error";
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useConvex, useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type {
  Id,
} from "@constructionautomation/backend/convex/_generated/dataModel";
import {
  Download,
  Eye,
  Loader2,
  Search,
  ScanSearch,
} from "lucide-react";
import { toast } from "sonner";

import { ConfirmationDialog } from "@/components/confirmation-dialog";
import { ViewerBackButton, ViewerTopBar } from "@/components/layout";
import { useViewerExit } from "@/components/layout/use-viewer-exit";
import { RunStatusBadge } from "@/components/run-status-badge";
import { ViewerMetaChip } from "@/components/semantic-badges";
import {
  LinkedRevisionJobsButton,
  type LinkedRevisionJob,
} from "@/components/linked-revision-jobs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { DocumentViewportHost } from "@/features/document-viewport/document-viewport-host";
import { getDocumentMediaKind } from "@/features/document-viewport/media";
import {
  DocumentViewportPageControls,
  DocumentViewportToolbar,
  DocumentViewportToolbarSection,
} from "@/features/document-viewport/toolbar";
import type { DocumentSource } from "@/features/document-viewport/types";
import {
  buildDocumentDownloadFilename,
  downloadUrlAsFile,
} from "@/lib/document-download";
import { useRevisionSheetLabels } from "@/features/sheet-index/use-revision-sheets";
import { cn } from "@/lib/utils";
import { DocumentTagEditorOverlay } from "@/features/document-intelligence/document-tag-editor-overlay";
import { useRecordActionLog } from "@/hooks/use-action-log-session";
import { useCanEdit } from "@/lib/capabilities";

interface RevisionRecord {
  _id: Id<"projectDocumentRevisions">;
  revisionNumber: number;
  revisionName: string;
  originalFilename: string;
  mimeType?: string;
  linkedJobs?: LinkedRevisionJob[];
}

type DetectionRecord = {
  _id: Id<"documentIntelligenceDetections">;
  pageNumber: number;
  rawText: string;
  normalizedText: string;
  classification: "finish_tag" | "room_tag" | "door_window_tag" | "reference_noise" | "unknown";
  bbox: { x: number; y: number; width: number; height: number };
  confidence?: number;
  isUserManaged?: boolean;
  createdByUserId?: Id<"users">;
};
type RunRecord = {
  _id: Id<"documentIntelligenceRuns">;
  status: "pending" | "processing" | "completed" | "failed";
  error?: string;
  indexedPageCount?: number;
  pageCount?: number;
};

const CONFIDENCE_OPTIONS = [
  { value: "0", label: "Any confidence" },
  { value: "0.5", label: "50%+" },
  { value: "0.7", label: "70%+" },
  { value: "0.85", label: "85%+" },
] as const;

const DETECTION_OVERLAY_STYLE = {
  color: "#6366F1",
  // Drawn over the PDF, so it needs a fixed hue that reads on any theme (matches `color`).
  // eslint-disable-next-line no-restricted-syntax
  fillClassName: "bg-indigo-500/15 text-indigo-100",
} as const;

function confidenceLabel(confidence: number | undefined) {
  if (confidence === undefined) {
    return "n/a";
  }
  return `${Math.round(confidence * 100)}%`;
}

interface DocumentIntelligenceViewportToolbarProps {
  activePage: number;
  activePageLabel: string | undefined;
  pageCount: number;
  detectionCount: number;
  overlaysVisible: boolean;
  setOverlaysVisible: (value: boolean) => void;
  searchText: string;
  setSearchText: (value: string) => void;
  minConfidence: string;
  setMinConfidence: (value: string) => void;
  indexStatus: RunRecord["status"] | undefined;
  indexedPageCount: number | undefined;
  totalPageCount: number | undefined;
}

function DocumentIntelligenceViewportToolbar({
  activePage,
  activePageLabel,
  pageCount,
  detectionCount,
  overlaysVisible,
  setOverlaysVisible,
  searchText,
  setSearchText,
  minConfidence,
  setMinConfidence,
  indexStatus,
  indexedPageCount,
  totalPageCount,
}: DocumentIntelligenceViewportToolbarProps) {
  return (
    <>
      <DocumentViewportToolbar className="gap-2 p-3">
          <DocumentViewportToolbarSection className="min-w-[180px]">
            <Eye className="size-4 text-muted-foreground" />
            <div className="flex items-center gap-2">
              <Switch
                checked={overlaysVisible}
                onCheckedChange={setOverlaysVisible}
                size="sm"
                aria-label="Show indexed tags"
              />
              <span className="text-sm font-medium">Show indexed tags</span>
            </div>
          </DocumentViewportToolbarSection>

          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchText}
              onChange={(event) => setSearchText(event.currentTarget.value)}
              placeholder="Filter detected text"
              className="ps-9"
              disabled={!overlaysVisible}
            />
          </div>

          <Select
            value={minConfidence}
            onValueChange={setMinConfidence}
            disabled={!overlaysVisible}
          >
            <SelectTrigger className="min-w-[160px] bg-background/70">
              <SelectValue placeholder="Confidence" />
            </SelectTrigger>
            <SelectContent>
              {CONFIDENCE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <DocumentViewportPageControls
            activePage={activePage}
            pageCount={pageCount}
            centerLabel={
              <div className="flex min-w-20 max-w-56 flex-col items-center text-center text-sm font-medium tabular-nums text-muted-foreground">
                <span>
                  {activePage}/{pageCount}
                </span>
                {activePageLabel ? (
                  <span className="max-w-full truncate text-xs font-normal">{activePageLabel}</span>
                ) : null}
              </div>
            }
          />
      </DocumentViewportToolbar>

      <div className="pointer-events-none absolute bottom-4 left-4 z-20 flex max-w-[calc(100%-2rem)] flex-wrap items-center gap-2">
        <ViewerMetaChip className="bg-background/95 tabular-nums backdrop-blur">
          Page {activePage} · {detectionCount} visible tag{detectionCount === 1 ? "" : "s"}
        </ViewerMetaChip>
        {indexStatus === "completed" && indexedPageCount !== undefined && totalPageCount !== undefined ? (
          <ViewerMetaChip className="bg-background/95 tabular-nums backdrop-blur">
            Indexed {indexedPageCount}/{totalPageCount} page{totalPageCount === 1 ? "" : "s"}
          </ViewerMetaChip>
        ) : null}
      </div>
    </>
  );
}

interface DocumentIntelligenceViewerProps {
  documentName: string;
  revisions: RevisionRecord[];
  initialRevisionId?: string | null;
}

interface ViewerIndexActionState {
  label: string;
  disabled: boolean;
  isReindex: boolean;
}

interface ViewerNoticeState {
  message: string;
  tone?: "default" | "destructive";
}

function getIndexActionState(
  latestRunStatus: RunRecord["status"] | undefined,
  hasSelectedRevision: boolean
): ViewerIndexActionState {
  return {
    label:
      latestRunStatus === "failed" || latestRunStatus === "completed"
        ? "Re-index"
        : latestRunStatus === "processing" || latestRunStatus === "pending"
          ? "Indexing"
          : "Index document",
    disabled:
      !hasSelectedRevision ||
      latestRunStatus === "processing" ||
      latestRunStatus === "pending",
    isReindex: latestRunStatus === "failed" || latestRunStatus === "completed",
  };
}

function getViewerNoticeState(args: {
  latestRun: RunRecord | null | undefined;
  overlaysVisible: boolean;
  isPageDetectionsPending: boolean;
  pageDetectionCount: number;
}): ViewerNoticeState | null {
  const { latestRun, overlaysVisible, isPageDetectionsPending, pageDetectionCount } = args;
  if (latestRun?.status === "failed") {
    return {
      message:
        latestRun.error ?? "The latest indexing run failed. Retry indexing to refresh tags.",
      tone: "destructive",
    };
  }
  if (latestRun?.status !== "completed") {
    return {
      message:
        latestRun?.status === "processing" || latestRun?.status === "pending"
          ? "Tag indexing is running for this revision. The document remains viewable while the index completes."
          : "Index this revision to surface detected tags directly in the viewer.",
    };
  }
  if (overlaysVisible && !isPageDetectionsPending && pageDetectionCount === 0) {
    return {
      message: "No indexed tags match the current filters on this page.",
    };
  }
  return null;
}

function useIndexDocumentAction(args: {
  selectedRevision: RevisionRecord | null;
  latestRunStatus: RunRecord["status"] | undefined;
  ensureIndex: (args: {
    revisionId: Id<"projectDocumentRevisions">;
  }) => Promise<{ reused: boolean; status: RunRecord["status"] }>;
  reindexDocument: (args: {
    revisionId: Id<"projectDocumentRevisions">;
  }) => Promise<{ reused: boolean; status: RunRecord["status"] }>;
  onAction?: () => void | Promise<void>;
}) {
  const { selectedRevision, latestRunStatus, ensureIndex, reindexDocument, onAction } = args;

  return useCallback(async () => {
    if (!selectedRevision) {
      return;
    }

    const isReindex = latestRunStatus === "completed" || latestRunStatus === "failed";

    try {
      const result = isReindex
        ? await reindexDocument({ revisionId: selectedRevision._id })
        : await ensureIndex({ revisionId: selectedRevision._id });

      if (result.reused) {
        toast.success(
          result.status === "completed"
            ? "Document is already indexed"
            : "Document indexing is already in progress"
        );
        return;
      }

      toast.success(isReindex ? "Document re-indexing started" : "Document indexing started");
      await onAction?.();
    } catch (error) {
      console.error(error);
      notifyError(error,
        isReindex
          ? "Failed to start document re-indexing"
          : "Failed to start document indexing"
      );
    }
  }, [ensureIndex, latestRunStatus, onAction, reindexDocument, selectedRevision]);
}

function LoadingOrErrorState({ downloadUrl }: { downloadUrl: string | null | undefined }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {downloadUrl === undefined ? (
        <Loader2 className="size-5 animate-spin" />
      ) : (
        "Unable to load this revision."
      )}
    </div>
  );
}

function ViewerFloatingNotice({
  children,
  tone = "default",
}: {
  children: string;
  tone?: "default" | "destructive";
}) {
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex justify-center px-4">
      <div
        className={cn(
          "pointer-events-auto max-w-xl rounded-xl border bg-background/95 px-4 py-3 text-sm shadow-lg backdrop-blur",
          tone === "destructive"
            ? "border-destructive/40 text-destructive"
            : "text-muted-foreground"
        )}
      >
        {children}
      </div>
    </div>
  );
}

function DocumentIntelligenceViewerHeader({
  onBack,
  documentName,
  selectedRevisionId,
  setSelectedRevisionId,
  selectedRevision,
  sortedRevisions,
  latestRunStatus,
  handleIndexDocument,
  handleCancelRun,
  isCancellingRun,
  indexActionState,
  onDownload,
  isDownloading,
  canDownload,
}: {
  onBack: () => void;
  documentName: string;
  selectedRevisionId: string;
  setSelectedRevisionId: (value: string) => void;
  selectedRevision: RevisionRecord | null;
  sortedRevisions: RevisionRecord[];
  latestRunStatus: RunRecord["status"] | undefined;
  handleIndexDocument: () => void;
  handleCancelRun: () => Promise<void>;
  isCancellingRun: boolean;
  indexActionState: ViewerIndexActionState;
  onDownload: () => void;
  isDownloading: boolean;
  canDownload: boolean;
}) {
  const canEdit = useCanEdit();
  return (
    <ViewerTopBar
      back={<ViewerBackButton label="Back" onBack={onBack} />}
      title={documentName}
      subtitle={selectedRevision ? `${selectedRevision.revisionName} (r${selectedRevision.revisionNumber})` : undefined}
      readOnly={!canEdit}
      source={
        <Select value={selectedRevisionId} onValueChange={setSelectedRevisionId}>
          <SelectTrigger className="h-8 w-72 shrink-0 bg-background" aria-label="Revision">
            <SelectValue placeholder="Select revision" />
          </SelectTrigger>
          <SelectContent>
            {sortedRevisions.map((revision) => (
              <SelectItem key={revision._id} value={String(revision._id)}>
                {`${revision.revisionName} (r${revision.revisionNumber}) · ${buildDocumentDownloadFilename(
                  documentName,
                  revision.originalFilename,
                  revision.revisionNumber
                )}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      }
      meta={<RunStatusBadge status={latestRunStatus} />}
      tools={
        <>
          <LinkedRevisionJobsButton jobs={selectedRevision?.linkedJobs} />

          {canEdit ? (
            <Button
              type="button"
              variant={latestRunStatus === "failed" ? "destructive" : "outline"}
              size="sm"
              onClick={handleIndexDocument}
              disabled={indexActionState.disabled}
            >
              {latestRunStatus === "processing" || latestRunStatus === "pending" ? (
                <Loader2 className="animate-spin" />
              ) : (
                <ScanSearch />
              )}
              {indexActionState.label}
            </Button>
          ) : null}

          {canEdit && (latestRunStatus === "processing" || latestRunStatus === "pending") ? (
            <ConfirmationDialog
              title="Cancel indexing run?"
              description="This will mark the current indexing run as failed so you can retry. The service may still be working in the background, but its later writes will be rejected."
              confirmLabel="Cancel run"
              cancelLabel="Keep running"
              confirmVariant="destructive"
              onConfirm={handleCancelRun}
            >
              <Button type="button" variant="destructive" size="sm" disabled={isCancellingRun}>
                {isCancellingRun ? <Loader2 className="animate-spin" /> : null}
                Cancel run
              </Button>
            </ConfirmationDialog>
          ) : null}
        </>
      }
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onDownload}
          disabled={!canDownload || isDownloading}
        >
          {isDownloading ? <Loader2 className="animate-spin" /> : <Download />}
          Download
        </Button>
      }
    />
  );
}

export function DocumentIntelligenceViewer({
  documentName,
  revisions,
  initialRevisionId,
}: DocumentIntelligenceViewerProps) {
  const onBack = useViewerExit({ fallback: { to: "/project/documents", title: "Project Documents" } });
  const convex = useConvex();
  const recordActionLog = useRecordActionLog();
  const ensureIndex = useMutation(api.documentIntelligence.runs.ensureForRevision);
  const reindexDocument = useMutation(api.documentIntelligence.runs.reindexForRevision);
  const cancelRun = useMutation(api.documentIntelligence.runs.cancel);
  const sortedRevisions = useMemo(
    () =>
      revisions
        .slice()
        .sort((a, b) =>
          b.revisionNumber - a.revisionNumber || String(b._id).localeCompare(String(a._id))
        ),
    [revisions]
  );
  const [selectedRevisionId, setSelectedRevisionId] = useState<string>(
    String(initialRevisionId ?? sortedRevisions[0]?._id ?? "")
  );
  const [activePage, setActivePage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [showIndexedTags, setShowIndexedTags] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [minConfidence, setMinConfidence] = useState<string>("0");
  const [isCancellingRun, setIsCancellingRun] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const deferredSearchText = useDeferredValue(searchText);

  useEffect(() => {
    if (
      initialRevisionId &&
      sortedRevisions.some((revision) => String(revision._id) === String(initialRevisionId))
    ) {
      setSelectedRevisionId(String(initialRevisionId));
    }
  }, [initialRevisionId, sortedRevisions]);

  useEffect(() => {
    setSelectedRevisionId((current) => {
      if (sortedRevisions.length === 0) {
        return "";
      }
      if (current && sortedRevisions.some((revision) => String(revision._id) === current)) {
        return current;
      }
      return String(sortedRevisions[0]?._id ?? "");
    });
  }, [sortedRevisions]);

  const selectedRevision = useMemo(
    () =>
      sortedRevisions.find((revision) => String(revision._id) === selectedRevisionId) ?? null,
    [selectedRevisionId, sortedRevisions]
  );

  useEffect(() => {
    setActivePage(1);
  }, [selectedRevisionId]);

  const { data: downloadUrl } = useQuery(
    convexQuery(
      api.projectDocuments.getRevisionDownloadUrl,
      selectedRevision ? { revisionId: selectedRevision._id } : "skip"
    )
  );
  const latestRunQuery = useQuery(
    convexQuery(
      api.documentIntelligence.runs.getLatestByRevision,
      selectedRevision ? { revisionId: selectedRevision._id } : "skip"
    )
  );

  const latestRun = latestRunQuery.data as RunRecord | null | undefined;
  const pageLabels = useRevisionSheetLabels(selectedRevision?._id);
  const overlaysVisible = showIndexedTags && Boolean(selectedRevision);
  const pageDetectionsQuery = useQuery(
    convexQuery(
      api.documentIntelligence.tags.listByRevisionPage,
      selectedRevision && showIndexedTags
        ? {
            revisionId: selectedRevision._id,
            pageNumber: activePage,
            searchText: deferredSearchText.trim() || undefined,
            minConfidence:
              minConfidence === "0" ? undefined : Number.parseFloat(minConfidence),
          }
        : "skip"
    )
  );
  const pageTagData = pageDetectionsQuery.data as
    | { activeDetections: DetectionRecord[]; archivedDetections: DetectionRecord[] }
    | undefined;
  const pageDetections = pageTagData?.activeDetections ?? [];
  const archivedDetections = pageTagData?.archivedDetections ?? [];

  const source = useMemo<DocumentSource | null>(() => {
    if (!selectedRevision || !downloadUrl) {
      return null;
    }
    const filename = buildDocumentDownloadFilename(
      documentName,
      selectedRevision.originalFilename,
      selectedRevision.revisionNumber
    );
    return {
      revisionId: String(selectedRevision._id),
      fileUrl: downloadUrl,
      filename,
      mimeType: selectedRevision.mimeType,
      mediaKind: getDocumentMediaKind(
        selectedRevision.originalFilename,
        selectedRevision.mimeType
      ),
    };
  }, [documentName, downloadUrl, selectedRevision]);

  const handleDownload = useCallback(async () => {
    if (!selectedRevision || isDownloading) {
      return;
    }

    setIsDownloading(true);
    try {
      const freshDownloadUrl = await convex.query(api.projectDocuments.getRevisionDownloadUrl, {
        revisionId: selectedRevision._id,
      });
      if (!freshDownloadUrl) {
        throw new Error("Download URL unavailable");
      }
      await downloadUrlAsFile(
        freshDownloadUrl,
        buildDocumentDownloadFilename(
          documentName,
          selectedRevision.originalFilename,
          selectedRevision.revisionNumber
        )
      );
      toast.success("Download started");
    } catch (error) {
      console.error(error);
      notifyError(error, error instanceof Error ? error.message : "Failed to download document");
    } finally {
      setIsDownloading(false);
    }
  }, [convex, documentName, isDownloading, selectedRevision]);

  const handleIndexDocument = useIndexDocumentAction({
    selectedRevision,
    latestRunStatus: latestRun?.status,
    ensureIndex,
    reindexDocument,
    onAction: recordActionLog,
  });
  const handleCancelRun = useCallback(async () => {
    if (!latestRun || isCancellingRun) {
      return;
    }

    setIsCancellingRun(true);
    try {
      const result = await cancelRun({ runId: latestRun._id });
      if (!result.canceled) {
        toast.error("This indexing run can no longer be canceled");
        return;
      }
      toast.success("Indexing run canceled");
      await recordActionLog();
    } catch (error) {
      console.error(error);
      notifyError(error, "Failed to cancel indexing run");
    } finally {
      setIsCancellingRun(false);
    }
  }, [cancelRun, isCancellingRun, latestRun, recordActionLog]);
  const indexActionState = getIndexActionState(latestRun?.status, Boolean(selectedRevision));
  const viewerNotice = getViewerNoticeState({
    latestRun,
    overlaysVisible,
    isPageDetectionsPending: pageDetectionsQuery.isPending,
    pageDetectionCount: pageDetections.length,
  });

  if (sortedRevisions.length === 0) {
    return (
      <div
        className="flex min-h-[320px] flex-1 items-center justify-center rounded-xl border bg-background text-sm text-muted-foreground"
      >
        This document has no revisions yet.
      </div>
    );
  }

  return (
    // Grows into the page-content flex column (see AppShell).
    <div className="flex min-h-[720px] flex-1 flex-col gap-4">
      <DocumentIntelligenceViewerHeader
        onBack={onBack}
        documentName={documentName}
        selectedRevisionId={selectedRevisionId}
        setSelectedRevisionId={setSelectedRevisionId}
        selectedRevision={selectedRevision}
        sortedRevisions={sortedRevisions}
        latestRunStatus={latestRun?.status}
        handleIndexDocument={() => void handleIndexDocument()}
        handleCancelRun={handleCancelRun}
        isCancellingRun={isCancellingRun}
        indexActionState={indexActionState}
        onDownload={() => void handleDownload()}
        isDownloading={isDownloading}
        canDownload={Boolean(selectedRevision)}
      />

      <div className="relative min-h-0 flex-1 overflow-hidden rounded-2xl border bg-background shadow-sm">
        {source ? (
          <DocumentViewportHost
            source={source}
            activePage={activePage}
            onActivePageChange={setActivePage}
            onPageCountChange={setPageCount}
            pageLabels={pageLabels}
            viewportPadding={{
              top: 104,
              right: 24,
              bottom: 28,
              left: 24,
            }}
            className="absolute inset-0 overflow-hidden"
          >
            {showIndexedTags && selectedRevision ? (
              <DocumentTagEditorOverlay
                revisionId={selectedRevision._id}
                activeDetections={pageDetections}
                archivedDetections={archivedDetections}
                getStyle={(detection) => ({
                  ...DETECTION_OVERLAY_STYLE,
                  badgeText: confidenceLabel(detection.confidence),
                })}
                showInlineLabel
                secondaryLabel={(detection) => confidenceLabel(detection.confidence)}
                onAction={recordActionLog}
              />
            ) : null}
            <DocumentIntelligenceViewportToolbar
              activePage={activePage}
              activePageLabel={pageLabels?.get(activePage)}
              pageCount={pageCount}
              detectionCount={pageDetections.length}
              overlaysVisible={showIndexedTags}
              setOverlaysVisible={setShowIndexedTags}
              searchText={searchText}
              setSearchText={setSearchText}
              minConfidence={minConfidence}
              setMinConfidence={setMinConfidence}
              indexStatus={latestRun?.status}
              indexedPageCount={latestRun?.indexedPageCount}
              totalPageCount={latestRun?.pageCount}
            />
          </DocumentViewportHost>
        ) : (
          <LoadingOrErrorState downloadUrl={downloadUrl} />
        )}

        {viewerNotice ? (
          <ViewerFloatingNotice tone={viewerNotice.tone}>
            {viewerNotice.message}
          </ViewerFloatingNotice>
        ) : null}

        {pageDetectionsQuery.isPending && overlaysVisible ? (
          <div className="pointer-events-none absolute right-4 top-24 z-20 rounded-md border bg-background/95 px-3 py-2 text-xs text-muted-foreground shadow-sm backdrop-blur">
            <div className="flex items-center gap-2">
              <Loader2 className="size-3.5 animate-spin" />
              Loading indexed tags
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
